/*
 * Copyright 2016 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __assign } from "tslib";
import classNames from "classnames";
import React, { forwardRef, useCallback, useRef, useState } from "react";
import { Classes, Keys, Utils } from "../../common";
import { DISPLAYNAME_PREFIX, removeNonHTMLProps } from "../../common/props";
import { mergeRefs } from "../../common/refs";
import { Icon } from "../icon/icon";
import { Spinner } from "../spinner/spinner";
export var Button = forwardRef(function (props, ref) {
    var commonAttributes = useSharedButtonAttributes(props, ref);
    return (React.createElement("button", __assign({ type: "button" }, removeNonHTMLProps(props), commonAttributes), renderButtonContents(props)));
});
Button.displayName = DISPLAYNAME_PREFIX + ".Button";
export var AnchorButton = forwardRef(function (props, ref) {
    var href = props.href, _a = props.tabIndex, tabIndex = _a === void 0 ? 0 : _a;
    var commonProps = useSharedButtonAttributes(props, ref);
    return (React.createElement("a", __assign({ role: "button" }, removeNonHTMLProps(props), commonProps, { href: commonProps.disabled ? undefined : href, tabIndex: commonProps.disabled ? -1 : tabIndex }), renderButtonContents(props)));
});
AnchorButton.displayName = DISPLAYNAME_PREFIX + ".AnchorButton";
/**
 * Most of the button logic lives in this shared hook.
 */
function useSharedButtonAttributes(props, ref) {
    var _a;
    var active = props.active, alignText = props.alignText, fill = props.fill, large = props.large, loading = props.loading, outlined = props.outlined, minimal = props.minimal, small = props.small, tabIndex = props.tabIndex;
    var disabled = props.disabled || loading;
    // the current key being pressed
    var _b = useState(), currentKeyDown = _b[0], setCurrentKeyDown = _b[1];
    // whether the button is in "active" state
    var _c = useState(false), isActive = _c[0], setIsActive = _c[1];
    // our local ref for the button element, merged with the consumer's own ref (if supplied) in this hook's return value
    var buttonRef = useRef(null);
    var handleBlur = useCallback(function (e) {
        var _a;
        if (isActive) {
            setIsActive(false);
        }
        (_a = props.onBlur) === null || _a === void 0 ? void 0 : _a.call(props, e);
    }, [isActive, props.onBlur]);
    var handleKeyDown = useCallback(function (e) {
        var _a;
        // HACKHACK: https://github.com/palantir/blueprint/issues/4165
        /* eslint-disable deprecation/deprecation */
        if (Keys.isKeyboardClick(e.which)) {
            e.preventDefault();
            if (e.which !== currentKeyDown) {
                setIsActive(true);
            }
        }
        setCurrentKeyDown(e.which);
        (_a = props.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(props, e);
    }, [currentKeyDown, props.onKeyDown]);
    var handleKeyUp = useCallback(function (e) {
        var _a, _b;
        // HACKHACK: https://github.com/palantir/blueprint/issues/4165
        /* eslint-disable deprecation/deprecation */
        if (Keys.isKeyboardClick(e.which)) {
            setIsActive(false);
            (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.click();
        }
        setCurrentKeyDown(undefined);
        (_b = props.onKeyUp) === null || _b === void 0 ? void 0 : _b.call(props, e);
    }, [props.onKeyUp]);
    var className = classNames(Classes.BUTTON, (_a = {},
        _a[Classes.ACTIVE] = !disabled && (active || isActive),
        _a[Classes.DISABLED] = disabled,
        _a[Classes.FILL] = fill,
        _a[Classes.LARGE] = large,
        _a[Classes.LOADING] = loading,
        _a[Classes.MINIMAL] = minimal,
        _a[Classes.OUTLINED] = outlined,
        _a[Classes.SMALL] = small,
        _a), Classes.alignmentClass(alignText), Classes.intentClass(props.intent), props.className);
    return {
        className: className,
        disabled: disabled,
        onBlur: handleBlur,
        onClick: disabled ? undefined : props.onClick,
        onKeyDown: handleKeyDown,
        onKeyUp: handleKeyUp,
        ref: mergeRefs(buttonRef, ref),
        tabIndex: disabled ? -1 : tabIndex,
    };
}
/**
 * Shared rendering code for button contents.
 */
function renderButtonContents(props) {
    var children = props.children, icon = props.icon, loading = props.loading, rightIcon = props.rightIcon, text = props.text;
    var hasTextContent = !Utils.isReactNodeEmpty(text) || !Utils.isReactNodeEmpty(children);
    return (React.createElement(React.Fragment, null,
        loading && React.createElement(Spinner, { key: "loading", className: Classes.BUTTON_SPINNER, size: Icon.SIZE_LARGE }),
        React.createElement(Icon, { key: "leftIcon", icon: icon }),
        hasTextContent && (React.createElement("span", { key: "text", className: Classes.BUTTON_TEXT },
            text,
            children)),
        React.createElement(Icon, { key: "rightIcon", icon: rightIcon })));
}
//# sourceMappingURL=buttons.js.map